import {IconButton} from '@dropbox/dig-components/buttons';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AddLine, ArrowRightLine} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {snackbarAtom} from 'atoms/snackbar';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {useState} from 'react';
import {useWorkstreamCreate} from 'views/workstreams/hooks';
import {buildDefaultWorkstreamPayload} from 'views/workstreams/WorkstreamCreate';

// Wide enough for the whole placeholder plus the arrow.
const NAME_INPUT_WIDTH_PX = 340;

// The "+" at the right end of the /workstreams toolbar. Clicking it asks for a
// name in place; nothing is saved until Enter (or the arrow), so Esc (or leaving
// the field empty) leaves no placeholder behind. `onCreated` then opens it.
export const NewWorkstreamButton = ({onCreated}: {onCreated: (id: number) => void}) => {
  const {createWorkstream, isPending} = useWorkstreamCreate();
  const {employee} = useAtomValue(loggedInEmployeeAtom);
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');

  const cancel = () => {
    setNaming(false);
    setName('');
  };

  const create = () => {
    const trimmed = name.trim();
    if (!trimmed || !employee.user_id || isPending) return;
    analyticsLogger().logEvent('WORKSTREAM_VIEW_CLICKED', {
      source: 'workstreams4_title_new_workstream',
      type: 'new_workstream',
      v4: true,
    });
    createWorkstream({
      data: {...buildDefaultWorkstreamPayload(employee.user_id, 'workstream'), name: trimmed},
    })
      .then((id) => {
        cancel();
        onCreated(id);
      })
      .catch((error: unknown) =>
        setSnackbarMessage({
          text: error instanceof Error && error.message ? error.message : 'Something went wrong',
        })
      );
  };

  if (naming) {
    return (
      <Box display="flex" alignItems="center" style={{width: NAME_INPUT_WIDTH_PX, minWidth: 0}}>
        <TextInput
          size="small"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          // "I don't have a name yet" shouldn't stop anyone: any working name will do.
          placeholder="Working name, you can rename it anytime"
          aria-label={t('new_workstream')}
          autoComplete="off"
          autoFocus
          disabled={isPending}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              create();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          // Clicking away keeps a typed name (Esc drops it); an empty field just closes.
          onBlur={() => {
            if (!name.trim()) cancel();
          }}
          // Shows how to finish without spelling out "press Enter" (which still works).
          withRightAccessory={
            <IconButton
              variant="transparent"
              size="small"
              aria-label={t('new_workstream')}
              disabled={!name.trim() || isPending}
              onClick={create}
            >
              <UIIcon src={ArrowRightLine} />
            </IconButton>
          }
        />
      </Box>
    );
  }

  return (
    <Tooltip title={t('new_workstream')}>
      <IconButton
        variant="transparent"
        size="small"
        aria-label={t('new_workstream')}
        onClick={() => setNaming(true)}
      >
        <UIIcon src={AddLine} />
      </IconButton>
    </Tooltip>
  );
};
